import type { ExploreImageView } from '@vizit/api';
import { Button, ButtonLevel, ButtonSize, Text, TextColor, TextSize } from '@vizit/atoms';
import { ArrowNarrowRightIcon } from '@vizit/icons';
import { Asset } from '@vizit/molecules';
import { ExploreClassificationHeader } from '@vizit/organisms';
import { cva, cx } from '@vizit/theme';
import * as React from 'react';
import { useHideExploreScores } from '../../hooks';
import { ExploreAsset } from '../explore-asset';

interface ImageClassificationPreviewProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Classification ID
   * @required
   */
  classificationId: string;
  /**
   * Classification name
   * @required
   */
  classificationName: string;
  /**
   * Classification description
   * @required
   */
  description: string;
  /**
   * Preview images to display
   * @required
   */
  images: ExploreImageView[];
  /**
   * Total count of images available for this classification
   * @optional
   * @default 0
   */
  totalCount?: number;
  /**
   * Number of skeleton items to show during loading
   * @required
   */
  previewLimit: number;
  /**
   * Whether the component is in loading state
   * @optional
   * @default false
   */
  isLoading?: boolean;
  /**
   * Callback when "Browse all" button is clicked
   * @required
   */
  onBrowseAll: () => void;
  /**
   * Callback when an image is clicked
   * @optional
   */
  onAssetClick?: (imageId: string) => void;
  /**
   * Custom CSS class for the container
   * @optional
   */
  className?: string;
}

const containerClasses = cva({
  base: 'flex flex-col gap-16',
});

const imageGridClasses = cva({
  base: 'grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-8',
});

const LoadingImageGrid = React.memo(({ count, showScoreBar }: { count: number; showScoreBar: boolean }) => {
  return (
    <>
      {Array.from({ length: count }, (_, index) => {
        const skeletonId = `classification-image-skeleton-${index}`;
        return (
          <div key={skeletonId} className="min-w-48 w-full">
            <Asset isLoading showScoreBar={showScoreBar}>
              <Asset.Image name="Loading..." src={null} aria-hidden={true} />
            </Asset>
          </div>
        );
      })}
    </>
  );
});

/**
 * ImageClassificationPreview displays a preview of images for a specific classification
 * with a header showing the classification name and description, and a CTA button to browse all.
 *
 * Features:
 * - Responsive image grid that adapts to viewport size
 * - Classification name and description header
 * - Browse all button with navigation
 * - Loading states with skeleton placeholders
 *
 * @example
 * <ImageClassificationPreview
 *   classificationId="lifestyle-id"
 *   classificationName="Lifestyle"
 *   description="Images showing the product in real-world use..."
 *   images={previewImages}
 *   categoryL1="food"
 *   categoryL4="snacks"
 * />
 */
export const ImageClassificationPreview: React.FC<ImageClassificationPreviewProps> = ({
  classificationId,
  classificationName,
  description,
  images,
  totalCount = 0,
  previewLimit,
  isLoading = false,
  onBrowseAll,
  onAssetClick,
  className,
  ...other
}) => {
  const hasImages = images.length > 0;
  const hasMoreImages = images.length < totalCount;
  const hideScores = useHideExploreScores();
  return (
    <div className={cx(containerClasses(), className)} {...other}>
      <div className="flex items-start justify-between">
        <ExploreClassificationHeader
          classificationName={classificationName}
          description={description}
          isLoading={isLoading}
        />
        {!isLoading && hasImages && hasMoreImages && (
          <Button
            level={ButtonLevel['Text-Primary']}
            size={ButtonSize.Sm}
            iconEnd={<ArrowNarrowRightIcon size={20} />}
            onPress={onBrowseAll}
            className="font-semibold"
            data-testid={`explore-browse-all-${classificationName.toLowerCase().replace(/\s+/g, '-')}`}
          >
            Browse all
          </Button>
        )}
      </div>

      <div className={imageGridClasses()}>
        {isLoading ? (
          <LoadingImageGrid count={previewLimit} showScoreBar={!hideScores} />
        ) : !hasImages ? (
          <Text size={TextSize.Md} color={TextColor.Tertiary}>
            No images found
          </Text>
        ) : (
          images.map((image) => (
            <div key={image.id} className="min-w-48 w-full">
              <ExploreAsset image={image} showScoreBar={!hideScores} onAssetClick={onAssetClick} />
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export type { ImageClassificationPreviewProps };
